import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { getImages } from "../projectImageSlider/ProjectImageSlider";
import ProjectLanguages from "../projectLanguages/ProjectLanguages";
import { useLanguage } from "../../context/LanguageContext";
import translations from "../../i18n/translations";
import "./ProjectModal.css";

function ModalGallery({ folder }) {
  const images = getImages(folder);
  const [active, setActive] = useState(0);

  if (!images.length) return null;

  const prev = () => setActive((i) => (i - 1 + images.length) % images.length);
  const next = () => setActive((i) => (i + 1) % images.length);

  return (
    <div className="pm-gallery">
      <div className="pm-gallery__main">
        <img
          src={images[active]}
          alt={`screenshot ${active + 1}`}
          className="pm-gallery__main-img"
        />
        {images.length > 1 && (
          <>
            <button className="pm-gallery__nav pm-gallery__nav--prev" onClick={prev}>
              &#8249;
            </button>
            <button className="pm-gallery__nav pm-gallery__nav--next" onClick={next}>
              &#8250;
            </button>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="pm-gallery__thumbs">
          {images.map((img, i) => (
            <img
              key={i}
              src={img}
              alt=""
              className={`pm-gallery__thumb${i === active ? " pm-gallery__thumb--active" : ""}`}
              onClick={() => setActive(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectModal({ repo, theme, onClose }) {
  const { lang } = useLanguage();
  const tLabels = translations[lang].projects.modalLabels;
  const projectIdx = parseInt((repo.id || "project-0").split("-")[1], 10);
  const tDetails = translations[lang].projects.details?.[projectIdx];

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const details = tDetails
    ? { ...repo.details, ...tDetails }
    : repo.details;

  const borderColor =
    theme.name === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";

  const portalTarget =
    document.getElementById("modal-root") || document.body;

  return ReactDOM.createPortal(
    <div className="pm-overlay" onClick={onClose}>
      <div
        className="pm-panel"
        style={{ backgroundColor: theme.themeColor, "--pm-border": borderColor }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="pm-close" onClick={onClose} style={{ color: theme.secondaryText }}>
          ✕
        </button>

        <div className="pm-header" style={{ borderBottomColor: borderColor }}>
          <h2 className="pm-title" style={{ color: theme.text }}>{repo.name}</h2>
          {details?.role && (
            <p className="pm-role" style={{ color: theme.secondaryText }}>
              <strong>{tLabels.role} —</strong> {details.role}
            </p>
          )}
          {repo.languages?.length > 0 && (
            <div className="pm-stack">
              <ProjectLanguages logos={repo.languages} theme={theme} />
            </div>
          )}
        </div>

        <div className="pm-body">
          {repo.screenshotsFolder && <ModalGallery folder={repo.screenshotsFolder} />}

          <div className="pm-details">
            {[
              { label: tLabels.about, key: "about" },
              { label: tLabels.problem, key: "problem" },
              { label: tLabels.solution, key: "solution" },
            ].map(({ label, key }) =>
              details?.[key] ? (
                <section key={key} className="pm-section">
                  <h4 className="pm-section-title">{label}</h4>
                  <p className="pm-section-text" style={{ color: theme.secondaryText }}>
                    {details[key]}
                  </p>
                </section>
              ) : null
            )}

            {details?.done?.length > 0 && (
              <section className="pm-section">
                <h4 className="pm-section-title">{tLabels.results}</h4>
                <ul className="pm-results-list">
                  {details.done.map((item, i) => (
                    <li key={i} style={{ color: theme.secondaryText }}>{item}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>,
    portalTarget
  );
}
